import '../styles/Table.scss';

import ContentError         from '@widesk-ui/components/ContentError';
import FilterBar            from '@widesk-ui/components/FilterBar';
import { InitialFields }    from '@widesk-ui/components/FilterBar';
import React                from 'react';
import TableStore           from '@widesk-ui/stores/TableStore';
import { TableLocale }      from 'antd/lib/table/interface';
import View                 from '@widesk-ui/components/View';
import { observer }         from 'mobx-react-lite';
import { useBox }           from '@widesk-ui/hooks/useBox';
import { ValueType }        from '@widesk-ui/components/Value';
import { Action }           from '@widesk-ui/components/ButtonActions';
import TableContent         from '@widesk-ui/components/table/TableContent';
import PositionTableContent from '@widesk-ui/components/table/PositionTableContent';

export type RowSelectionConfig<D extends any[]> = {
	onChange?: (selectedRowKeys: React.Key[], selectedRows: D[]) => void;
	selectedRowKeys: React.Key[];
	getCheckboxProps?: (d: D[number]) => {
		disabled?: boolean;
	}
}

export type RowExpandableConfig<D extends any[]> = {
	checkStrictly?: boolean;
	defaultExpandAllRows?: boolean;
	expandedRowRender: (d: D[number]) => React.ReactNode;
	onExpand?: (expanded: boolean, d: D[number]) => void;
	rowExpandable?: (d: D[number]) => boolean;
	expandRowByClick?: boolean;
	expandedRowKeys?: any[];
}

export type Column<D> = {
	reference?: string;
	align?: 'right' | 'left' | 'center';
	title: React.ReactNode | string;
	type?: ValueType;
	sortBy?: string;
	sortOrder?: 'ascend' | 'descend';
	style?: (datum: D, index: number) => React.CSSProperties;
	hidden?: boolean;
	width?: number | string;
	fixed?: 'right' | 'left';
} & ({
	render?: undefined;
	children: Column<D>[], // Permet d'afficher des colonnes groupées
} | {
	render: (datum: D, index: number) => React.ReactNode | Dayjs;
	children?: undefined,
})

type ColumnData<D extends any[]> = Column<D[number]>;

export type TableMode = 'default' | 'vertical' | 'vertical-col';

export type TableProps<D extends any[], F extends InitialFields> = {
	columns: ColumnData<D>[];
	size?: ComponentSize;
	bordered?: boolean;
	headerHidden?: boolean;
	fetch: (page: number, step: number, filters: Record<string, unknown>) => Promise<D>;
	filters?: F;
	pagination?: Pagination | false;
	storageKey?: string;
	rowKey: (d: D[number]) => string;
	onDidMount?: (tableStore: TableStore) => void;
	onFetchStart?: (tableStore: TableStore) => Promise<void> | void;
	onFetchEnd?: (tableStore: TableStore) => Promise<void> | void;
	mode?: TableMode;
	defaultFilters?: Record<string, unknown>;
	actions?: (d: D[number], tableStore: TableStore) => Action[];
	loading?: boolean;
	locale?: TableLocale;
	renderHeader?: (tableStore: TableStore) => React.ReactNode;
	renderFooter?: (tableStore: TableStore) => React.ReactNode;
	rowExpandable?: RowExpandableConfig<D>;
	rowSelection?: RowSelectionConfig<D>;
	scroll?: { x?: number, y?: number; };
	components?: any;
	onRow?: any;
	sortable?: boolean;
	onChangePosition?: (data: {
		position: number;
		oldPosition: number;
		store: TableStore;
	}) => void;
	urlStorageEnabled?: boolean;
}

const Table = observer(<D extends any[], F extends InitialFields>(props: TableProps<D, F>) => {
	const tableStore = React.useMemo(() => new TableStore(props), []);

	const { boxStore } = useBox();

	const mode = props.mode;
	const headerHidden = props.headerHidden || mode === 'vertical' || mode === 'vertical-col';

	const isEditablePosition = props.sortable;

	if (props.filters && tableStore.filters) {
		Object.entries(tableStore.filters).map(([key, value]) => {
			const initialFilter = props.filters?.[key];
			if (initialFilter) {
				initialFilter.value = value;
			}
		});
	}

	return (
		<View className={['widesk-table', headerHidden ? 'header-hidden' : '']} flex>
			{!boxStore && props.filters && !!Object.keys(props.filters || {}).length && (
				<FilterBar
					initialFields={props.filters}
					onSubmit={(values) => tableStore.fetch(1, tableStore.pageSize, { ...tableStore.sorts, ...values })}
				/>
			)}

			{!!tableStore.error && <ContentError error={tableStore.error} retry={() => tableStore.reload()} />}

			{!tableStore.error && (() => {
				if (isEditablePosition) {
					return <PositionTableContent tableStore={tableStore} {...props} />;
				}

				return <TableContent tableStore={tableStore} {...props} />;
			})()}
		</View>
	);
});

export default Table;
